export interface User {
  id: number
  full_name?: string
}

export interface UserListItem {
  id: number
  email: string
  username: string
  name: string
  first_name: string
  last_name: string
  is_active: boolean
  is_staff: boolean
  is_superuser: boolean
  updated_at: string
  last_sign_in_at: string | null
  role: string | null
}

export interface UserPbxAssociation {
  id: number
  pbx_id: number
  pbx_name: string
  sending_fax_email: string
  sending_fax_number: string
  pin: number | null
  fax_title: string
  user_groups: number[]
  assigned_extension_count?: number
  assigned_device_count?: number
}

/** Shape returned by GET /app/v2/users/{userId}/pbxes/. */
export interface UserAssociatedPbx {
  id: number
  name: string
  sending_fax_email?: string | null
  sending_fax_number?: string | null
  pin?: number | null
  fax_title?: string | null
  user_groups?: number[]
}

export interface UserDetail extends UserListItem {
  uuid: string
  created_at: string
  deleted_at: string | null
  sign_in_count: number
  current_sign_in_ip: string | null
  last_sign_in_ip: string | null
  valid_login: boolean
  last_login: string | null
  cognito_metadata: Record<string, unknown> | string | null
  date_joined: string
  remember_created_at: string | null
  current_sign_in_at: string | null
  last_sign_in_at: string | null
  pbx_users: UserPbxAssociation[]
}

export interface UserCreate {
  first_name: string
  last_name: string
  username: string
  email?: string
  is_staff?: boolean
  is_superuser?: boolean
  role?: number | null
  pbx: number
  pin?: number | null
  sending_fax_email?: string
  sending_fax_number?: string
  fax_title?: string
  pbx_user_groups?: number[]
}

export interface UserUpdate {
  first_name?: string
  last_name?: string
  /**
   * Email can only be set on an update when the user previously had no
   * email (legacy accounts). The backend enforces immutability once set.
   */
  email?: string
  role?: number | null
  is_staff?: boolean
  is_superuser?: boolean
  is_active?: boolean
}

export interface PbxUser {
  id: number
  user: UserListItem
  sending_fax_email: string
  sending_fax_number: string
  pin: number | null
  fax_title: string
  user_groups: number[]
}

export interface PbxUserCreate {
  user: number
  pin?: number | null
  sending_fax_email?: string
  sending_fax_number?: string
  fax_title?: string
  user_groups?: number[]
}

export interface PbxUserUpdate {
  pin?: number | null
  sending_fax_email?: string
  sending_fax_number?: string
  fax_title?: string
  user_groups?: number[]
}

export interface UserRole {
  id: number
  resource_type?: string | null
  type?: string | null
  name?: string | null
}

export interface UserAssignedRole {
  name: string
  type: string
}

export interface UserAssignedRolesResponse {
  username: string
  email: string
  is_staff: boolean
  is_superuser: boolean
  roles: UserAssignedRole[]
}

/** Current user profile for My Profile edit modal (legacy listUser shape) */
export interface MyProfile {
  id: number
  user_name: string
  username: string
  email: string
  first_name: string
  last_name: string
  valid_login?: number
}

/** Payload for updating my profile (legacy parity: first/last only; backend PATCH /app/v2/users/{id}/) */
export interface UpdateMyProfilePayload {
  first_name: string
  last_name: string
}

/** Raw shape from GET /app/v2/users/{id}/ (backend may use `name` instead of `user_name`). */
export interface UserDetailResponse {
  id: number
  username?: string
  email?: string
  name?: string
  first_name?: string
  last_name?: string
}

/** Maps backend user-detail response to MyProfile. Use in app adapters for getMyProfile. */
export function mapUserDetailToMyProfile(raw: UserDetailResponse): MyProfile {
  const first_name = raw.first_name ?? ''
  const last_name = raw.last_name ?? ''
  const user_name =
    (raw.name ?? [first_name, last_name].filter(Boolean).join(' ').trim()) ||
    (raw.username ?? '')
  return {
    id: raw.id,
    user_name,
    username: raw.username ?? '',
    email: raw.email ?? '',
    first_name,
    last_name,
  }
}
